import {Translatable} from 'common/i18n';

export interface SelectEventItem {
  item: EventItem | EventMenuItem;
}
export enum ActionType {
  Select,
  TriggerModal,
  Refresh,
  CreateNew,
}

export enum ModalType {
  CreateCustomEvent,
}

export type EventTagMenuItem = {
  /** the number of matching items */
  count: number;

  /** The value of the item in context of the menu */
  value: string;
};

/* [ActionType.TriggerModal] Items which result in a triggered modal */
export type TriggerModalItem<T extends ModalType = ModalType> = {
  /** The menu type to which the Item is opened */
  readonly action: ActionType.TriggerModal;

  /** Defines the type of modal to render */
  readonly modalType: T;
};

/* [ActionType.Refresh] Item to refresh events in menu */
export type RefreshMenuItem = {
  readonly action: ActionType.Refresh;
};

export type CreateNewMenuItem = {
  readonly action: ActionType.CreateNew;
};

export type ItemTriggerCustomEventModal = TriggerModalItem<ModalType.CreateCustomEvent> & {
  /* The header of the presented modal */
  label: string;
};

/* [ActionType.Select] Items which result in an actual selection. To preserve historical integrations, this can also be undefined */
export type SelectItem = {
  /** The menu type to which the Item is opened */
  readonly action?: ActionType.Select;

  /** An optional prefix to the event label */
  labelPrefix?: string;

  /** We should not need this item */
  name?: string;
};

export type Event = {
  /** The API value of the event */
  value: any;

  /** id of the custom event */
  id?: number;

  /** Specifies if the event is a custom event */
  custom?: boolean;

  icon?: string;

  /** Specifies if the event is a collect everything event */
  isCollectEverything?: boolean;

  /** The display name of the event */
  label: Translatable;

  /** The renamed label for the event */
  renamed?: string;

  /** Specifies if this event item is in the recently used list */
  isRecentlyUsed?: boolean;

  /** Specifies its category */
  subLabel?: string;

  /** Tags that are applied to the event */
  tags?: string[];

  /** Tags whether or not the event was generated by user */
  itemFromInput?: boolean;

  /** Whether or not the event is verified */
  verified?: boolean;

  /** The datetime of the last verification */
  lastVerified?: string;

  /** The user who last verified the event */
  lastVerifiedBy?: string;
};

export type EventItem = Event & SelectItem;

export type EventMenuItem = EventItem | ItemTriggerCustomEventModal | RefreshMenuItem | CreateNewMenuItem;

/**
 * This boolean map is used to filter a MenuItem to only the keys in Event
 */
export const SerializedEventItemKeyMap: {
  [P in keyof Required<Event>]: true;
} = {
  id: true,
  custom: true,
  label: true,
  value: true,
  renamed: true,
  isCollectEverything: true,
  isRecentlyUsed: true,
  subLabel: true,
  tags: true,
  icon: true,
  itemFromInput: true,
  verified: true,
  lastVerified: true,
  lastVerifiedBy: true,
};

export enum SidebarItemType {
  Category = `category`,
  EventTag = `event_tag`,
  EventTagAdder = `event_tag_adder`,
}

export enum SidebarCategory {
  All = `all`,
}

export type SidebarItem =
  | {type: SidebarItemType.Category; category: SidebarCategory}
  | {type: SidebarItemType.EventTag; tag: string}
  | {type: SidebarItemType.EventTagAdder};
